import { ExerciseStateMachine } from './ExerciseStateMachine';

// Convert to proper frozen enum pattern
export const ArmAbductionState = Object.freeze({
  DOWN: 'DOWN',
  ABDUCTING: 'ABDUCTING',
  UP: 'UP',
  LOWERING: 'LOWERING'
});

// Thresholds for "wings flapping" motion - reduced thresholds for easier detection
const MOVEMENT_THRESHOLD = 0.3; // Lower threshold to detect smaller movements
const MIN_TIME_IN_POSITION = 30; // Shorter time requirement
// Key angle thresholds for full flapping motion
const MIN_ABDUCTION_ANGLE = 5;  // Arms must start below shoulders - lower threshold 
const TARGET_ABDUCTION_ANGLE = 120;  // Arms should go past shoulder height
const MAX_ABDUCTION_ANGLE = 170;
const ANGLE_DIFF_THRESHOLD = 25;  // Allow more asymmetry
// Threshold to distinguish lateral vs forward movement
const LATERAL_RATIO_THRESHOLD = 0.4;  // Relaxed to detect more movements
const CONSECUTIVE_INVALID_FRAMES = 15; // More tolerance

// Set DEBUG to true to see output
const DEBUG = true;
const VERBOSE_DEBUG = true; // Set to true for even more detailed logs

export class ArmAbductionFSM extends ExerciseStateMachine {
  constructor() {
    const config = {
      initialState: ArmAbductionState.DOWN,
      initialStateData: {
        previousLeftAngle: 0,
        previousRightAngle: 0,
        isMovingOut: false,
        isMovingIn: false,
        lastPositions: [],
        lastArmsDownTime: 0,
        lastArmsUpTime: 0,
        exerciseProgress: 0,
        repFormScore: 0,
        invalidMotionCount: 0,
        stableUpFrames: 0,
        stableDownFrames: 0,
        calibrated: false,
        referenceShoulderWidth: 0,
        baselineLeftAngle: 0,
        baselineRightAngle: 0,
        maxAngleReached: 0,
        maxRangeOfMotion: 0, // Track maximum ROM achieved
        consecutiveEmptyFrames: 0,
        lastValidMetrics: null,
        frameCount: 0,
        wristsAboveShoulder: false,
        wristsBelowShoulder: true,
        debugFrameCounter: 0
      },
      initialFormScores: {
        overall: 100,
        rangeOfMotion: 0,
        symmetry: 100,
        control: 100,
        balance: 100
      },
      transitions: {
        [ArmAbductionState.DOWN]: [
          {
            targetState: ArmAbductionState.ABDUCTING,
            condition: (metrics, stateData, timestamp) => {
              // Relaxed condition - only require movement upward
              const result = metrics.isMovingOut && 
                metrics.avgAbductionAngle > MIN_ABDUCTION_ANGLE;
              
              if (DEBUG && result) {
                console.debug('DOWN→ABDUCTING', 
                  metrics.avgAbductionAngle.toFixed(1), 
                  metrics.isMovingOut);
              }
              return result;
            },
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastArmsDownTime = timestamp;
              fsm.stateData.stableDownFrames = 0;
              fsm.stateData.maxAngleReached = metrics.avgAbductionAngle;
              if (DEBUG) console.debug('Action: DOWN→ABDUCTING');
            }
          }
        ],
        
        [ArmAbductionState.ABDUCTING]: [
          {
            targetState: ArmAbductionState.UP,
            condition: (metrics, stateData, timestamp) => {
              // Easier condition to reach UP state
              const result = metrics.avgAbductionAngle > TARGET_ABDUCTION_ANGLE * 0.6 && 
                timestamp - stateData.lastArmsDownTime > MIN_TIME_IN_POSITION;
              
              if (DEBUG && result) {
                console.debug('ABDUCTING→UP', 
                  metrics.avgAbductionAngle.toFixed(1), 
                  (timestamp - stateData.lastArmsDownTime));
              }
              return result;
            },
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastArmsUpTime = timestamp;
              fsm.stateData.maxAngleReached = Math.max(
                fsm.stateData.maxAngleReached || 0,
                metrics.avgAbductionAngle
              );
              if (DEBUG) console.debug('Action: ABDUCTING→UP, MAX:', fsm.stateData.maxAngleReached.toFixed(1));
            },
            completesRepetition: true,
            calculateRepScore: (metrics, stateData) => {
              // Use MAX ROM achieved during rep, not current ROM at completion moment
              const maxROM = stateData.maxRangeOfMotion || metrics.rangeOfMotion || 0;
              return Math.round(
                (maxROM * 0.4) + 
                (metrics.symmetryScore * 0.3) + 
                (metrics.lateralScore * 0.3)
              );
            }
          },
          {
            targetState: ArmAbductionState.DOWN,
            condition: (metrics, stateData) => {
              const result = metrics.isMovingIn && metrics.avgAbductionAngle < MIN_ABDUCTION_ANGLE;
              if (DEBUG && result) {
                console.debug('ABDUCTING→DOWN', 
                  metrics.avgAbductionAngle.toFixed(1), 
                  metrics.isMovingIn);
              }
              return result;
            }
          }
        ],
        
        [ArmAbductionState.UP]: [
          {
            targetState: ArmAbductionState.LOWERING,
            condition: (metrics, stateData) => {
              const result = metrics.isMovingIn && 
                metrics.avgAbductionAngle < stateData.maxAngleReached * 0.9;
              
              if (DEBUG && result) {
                console.debug('UP→LOWERING', 
                  metrics.avgAbductionAngle.toFixed(1), 
                  stateData.maxAngleReached.toFixed(1), 
                  metrics.isMovingIn);
              }
              return result;
            },
            action: (fsm) => {
              fsm.stateData.stableUpFrames = 0;
              if (DEBUG) console.debug('Action: UP→LOWERING');
            }
          },
          {
            targetState: ArmAbductionState.DOWN,
            condition: (metrics, stateData) => {
              const result = metrics.avgAbductionAngle < MIN_ABDUCTION_ANGLE;
              if (DEBUG && result) {
                console.debug('UP→DOWN', 
                  metrics.avgAbductionAngle.toFixed(1));
              }
              return result;
            }
          }
        ],
        
        [ArmAbductionState.LOWERING]: [
          {
            targetState: ArmAbductionState.DOWN,
            condition: (metrics, stateData) => {
              // Easier condition to complete a rep
              const result = metrics.avgAbductionAngle < MIN_ABDUCTION_ANGLE * 1.5;
              if (DEBUG && result) {
                console.debug('LOWERING→DOWN', 
                  metrics.avgAbductionAngle.toFixed(1));
              }
              return result;
            },
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastArmsDownTime = timestamp;
              if (DEBUG) console.debug('Action: LOWERING→DOWN - COMPLETED REP');
            },
            completesRepetition: true,
            calculateRepScore: (metrics, stateData) => {
              // Use MAX ROM achieved during rep, not current ROM at completion moment
              const maxROM = stateData.maxRangeOfMotion || metrics.rangeOfMotion || 0;
              return Math.round(
                (maxROM * 0.4) + 
                (metrics.symmetryScore * 0.3) + 
                (metrics.lateralScore * 0.3)
              );
            }
          },
          {
            targetState: ArmAbductionState.ABDUCTING,
            condition: (metrics, stateData) => {
              const result = metrics.isMovingOut && 
                metrics.avgAbductionAngle > MIN_ABDUCTION_ANGLE * 1.1;
              
              if (DEBUG && result) {
                console.debug('LOWERING→ABDUCTING', 
                  metrics.avgAbductionAngle.toFixed(1), 
                  metrics.isMovingOut);
              }
              return result;
            }
          }
        ]
      }
    };
    
    super(config);
    
    this.constructor.initialConfig = {
      initialState: config.initialState,
      initialStateData: { ...config.initialStateData },
      initialFormScores: { ...config.initialFormScores }
    };
    
    if (DEBUG) console.debug('ArmAbductionFSM initialized - DEBUG mode ON');
  }
  
  computeMetrics(landmarks, worldLandmarks, timestamp) {
    // Track frame count for logging
    this.stateData.frameCount = (this.stateData.frameCount || 0) + 1;
    this.stateData.debugFrameCounter = (this.stateData.debugFrameCounter || 0) + 1;
    
    // More frequent logging for diagnosis
    const shouldLogDebug = DEBUG && (
      VERBOSE_DEBUG ? 
      this.stateData.debugFrameCounter % 10 === 0 : // More frequent in verbose mode
      this.stateData.frameCount % 30 === 0
    );
    
    // Handle empty landmarks gracefully
    if (!worldLandmarks || !Array.isArray(worldLandmarks) || worldLandmarks.length < 17) {
      this.stateData.consecutiveEmptyFrames = (this.stateData.consecutiveEmptyFrames || 0) + 1;
      
      if (shouldLogDebug) {
        console.debug(`Empty landmarks detected (${this.stateData.consecutiveEmptyFrames} consecutive frames)`);
      }
      
      // If we have previous valid metrics, use them with small decay to prevent jarring transitions
      if (this.stateData.lastValidMetrics && this.stateData.consecutiveEmptyFrames < 60) {
        const decayFactor = Math.max(0.5, 1 - (this.stateData.consecutiveEmptyFrames * 0.02));
        const metrics = {...this.stateData.lastValidMetrics};
        
        // Apply decay to movement-related metrics
        metrics.isMovingOut = false;
        metrics.isMovingIn = false;
        metrics.avgAbductionAngle *= decayFactor;
        metrics.leftAbductionAngle *= decayFactor;
        metrics.rightAbductionAngle *= decayFactor;
        metrics.previousTimestamp = timestamp;
        
        return metrics;
      }
      
      // Return minimal metrics to prevent crashes
      return {
        avgAbductionAngle: 0,
        leftAbductionAngle: 0,
        rightAbductionAngle: 0,
        angleDifference: 0,
        isMovingOut: false,
        isMovingIn: false,
        rangeOfMotion: 0,
        symmetryScore: 100,
        lateralScore: 100,
        lateralRatio: 1.0,
        wristsAboveShoulder: false,
        wristsBelowShoulder: true,
        previousTimestamp: timestamp
      };
    }
    
    // Reset consecutive empty frames counter
    this.stateData.consecutiveEmptyFrames = 0;
    
    try {
      // Safely extract key body landmarks with fallbacks for missing landmarks
      const nose = worldLandmarks[0] || { x: 0, y: 0, z: 0 };
      const leftShoulder = worldLandmarks[11] || { x: -0.2, y: 0, z: 0 };
      const rightShoulder = worldLandmarks[12] || { x: 0.2, y: 0, z: 0 };
      const leftElbow = worldLandmarks[13] || { x: -0.3, y: 0.15, z: 0 };
      const rightElbow = worldLandmarks[14] || { x: 0.3, y: 0.15, z: 0 };
      const leftWrist = worldLandmarks[15] || { x: -0.4, y: 0.3, z: 0 };
      const rightWrist = worldLandmarks[16] || { x: 0.4, y: 0.3, z: 0 };
      
      // Use hips if available, otherwise estimate based on shoulders
      const leftHip = worldLandmarks[23] || { 
        x: leftShoulder.x, 
        y: leftShoulder.y + 0.3, 
        z: leftShoulder.z 
      };
      const rightHip = worldLandmarks[24] || { 
        x: rightShoulder.x, 
        y: rightShoulder.y + 0.3, 
        z: rightShoulder.z 
      };
      
      // Get shoulder center and width for reference
      const shoulderCenter = {
        x: (leftShoulder.x + rightShoulder.x) / 2,
        y: (leftShoulder.y + rightShoulder.y) / 2,
        z: (leftShoulder.z + rightShoulder.z) / 2
      };
      
      const shoulderWidth = Math.sqrt(
        Math.pow(rightShoulder.x - leftShoulder.x, 2) +
        Math.pow(rightShoulder.y - rightShoulder.y, 2) +
        Math.pow(rightShoulder.z - leftShoulder.z, 2)
      );
      
      if (shouldLogDebug) {
        console.debug(`Shoulder width: ${shoulderWidth.toFixed(3)}`);
      }
      
      // Store reference shoulder width for calibration if needed
      if (!this.stateData.calibrated && shoulderWidth > 0.1) {
        this.stateData.referenceShoulderWidth = shoulderWidth;
        this.stateData.calibrated = true;
      }
      
      // Calculate vectors from shoulder to wrist for wing-flapping motion
      const leftArmX = leftWrist.x - leftShoulder.x;
      const leftArmY = leftWrist.y - leftShoulder.y;
      const rightArmX = rightWrist.x - rightShoulder.x;
      const rightArmY = rightWrist.y - rightShoulder.y;
      
      // Calculate the angle from vertical using atan2(x, y)
      // This gives us the angle in radians, conversion to degrees
      let leftAbductionAngle = Math.abs(Math.atan2(leftArmX, leftArmY) * 180 / Math.PI);
      let rightAbductionAngle = Math.abs(Math.atan2(rightArmX, rightArmY) * 180 / Math.PI);
      
      // Debug logs showing raw angles
      if (VERBOSE_DEBUG && shouldLogDebug) {
        console.debug(`Raw angles - Left: ${leftAbductionAngle.toFixed(1)}°, Right: ${rightAbductionAngle.toFixed(1)}°`);
      }
      
      // Check for correct arm direction
      const isLeftArmDirectionCorrect = leftArmX < 0;
      const isRightArmDirectionCorrect = rightArmX > 0;
      
      // Calculate lateral ratio (sideways movement)
      const leftLateralRatio = Math.abs(leftArmX) / (Math.abs(leftArmX) + Math.abs(leftWrist.z - leftShoulder.z) + 0.001);
      const rightLateralRatio = Math.abs(rightArmX) / (Math.abs(rightArmX) + Math.abs(rightWrist.z - rightShoulder.z) + 0.001);
      
      // Apply direction penalty more gently
      if (!isLeftArmDirectionCorrect) {
        leftAbductionAngle *= 0.5;
      }
      
      if (!isRightArmDirectionCorrect) {
        rightAbductionAngle *= 0.5;
      }
      
      // Apply smoothing for stable detection
      const smoothingFactor = 0.4;
      const prevLeftAngle = this.stateData.previousLeftAngle || leftAbductionAngle;
      const prevRightAngle = this.stateData.previousRightAngle || rightAbductionAngle;
      
      const smoothedLeftAngle = prevLeftAngle * (1 - smoothingFactor) + leftAbductionAngle * smoothingFactor;
      const smoothedRightAngle = prevRightAngle * (1 - smoothingFactor) + rightAbductionAngle * smoothingFactor;
      
      // Calculate average abduction angle and difference between arms
      const avgAbductionAngle = (smoothedLeftAngle + smoothedRightAngle) / 2;
      const angleDifference = Math.abs(smoothedLeftAngle - smoothedRightAngle);
      
      // Check if wrists are above or below shoulders (critical for wing-flapping motion)
      // More lenient Y-axis comparison (0.02 instead of 0.05)
      const leftWristAboveShoulder = leftWrist.y < leftShoulder.y - 0.02; 
      const rightWristAboveShoulder = rightWrist.y < rightShoulder.y - 0.02;
      const wristsAboveShoulder = leftWristAboveShoulder && rightWristAboveShoulder;
      
      const leftWristBelowShoulder = leftWrist.y > leftShoulder.y + 0.02; 
      const rightWristBelowShoulder = rightWrist.y > rightShoulder.y + 0.02;
      const wristsBelowShoulder = leftWristBelowShoulder && rightWristBelowShoulder;
      
      // Detailed wrist position debug
      if (VERBOSE_DEBUG && shouldLogDebug) {
        console.debug(`Wrist positions - Left(${leftWrist.y.toFixed(3)} vs ${leftShoulder.y.toFixed(3)}), ` +
                      `Right(${rightWrist.y.toFixed(3)} vs ${rightShoulder.y.toFixed(3)})`);
        console.debug(`Wrist states - Left above: ${leftWristAboveShoulder}, below: ${leftWristBelowShoulder}, ` +
                      `Right above: ${rightWristAboveShoulder}, below: ${rightWristBelowShoulder}`);
      }
      
      // Calculate symmetry score for proper form
      const symmetryScore = Math.max(0, 100 - (angleDifference * 100 / TARGET_ABDUCTION_ANGLE));
      
      // Calculate lateral score as average of left and right lateral ratios (0-100)
      const lateralRatio = (leftLateralRatio + rightLateralRatio) / 2;
      const lateralScore = Math.min(100, lateralRatio * 100);
      
      // Determine movement direction
      const avgPrevAngle = (this.stateData.previousLeftAngle + this.stateData.previousRightAngle) / 2 || avgAbductionAngle;
      const angleDelta = avgAbductionAngle - avgPrevAngle;
      const isMovingOut = angleDelta > MOVEMENT_THRESHOLD;
      const isMovingIn = angleDelta < -MOVEMENT_THRESHOLD;
      
      // Track position stability for more accurate state transitions
      let stableUpFrames = this.stateData.stableUpFrames || 0;
      let stableDownFrames = this.stateData.stableDownFrames || 0;
      
      if (avgAbductionAngle > TARGET_ABDUCTION_ANGLE * 0.9 && Math.abs(angleDelta) < MOVEMENT_THRESHOLD) {
        stableUpFrames++;
      } else if (avgAbductionAngle > TARGET_ABDUCTION_ANGLE * 0.7) {
        stableUpFrames = Math.max(0, stableUpFrames - 1);
      }
      
      if (avgAbductionAngle < MIN_ABDUCTION_ANGLE * 1.2 && Math.abs(angleDelta) < MOVEMENT_THRESHOLD) {
        stableDownFrames++;
      } else if (avgAbductionAngle > MIN_ABDUCTION_ANGLE * 2) {
        stableDownFrames = 0;
      }
      
      // Calculate velocity for form scoring
      let velocityScore = 100;
      const timeDelta = timestamp - (this.stateData.previousTimestamp || timestamp);
      
      if (timeDelta > 0) {
        const velocity = Math.abs(angleDelta) / (timeDelta / 1000);
        velocityScore = Math.min(100, Math.max(0, 100 - Math.abs((velocity - 45) / 45) * 100));
      }
      
      // Calculate range of motion relative to target angle
      const rangeOfMotion = Math.min(100, Math.max(0, (avgAbductionAngle / TARGET_ABDUCTION_ANGLE) * 100));
      
      // Track invalid motion
      let invalidMotionCount = this.stateData.invalidMotionCount || 0;
      if ((angleDifference > ANGLE_DIFF_THRESHOLD * 1.5 || lateralRatio < LATERAL_RATIO_THRESHOLD * 0.6) 
          && avgAbductionAngle > 40) {
        invalidMotionCount += 1;
      } else {
        invalidMotionCount = Math.max(0, invalidMotionCount - 1);
      }
      
      // Calculate arm straightness
      const leftElbowBend = this.calculateJointAngle(leftShoulder, leftElbow, leftWrist);
      const rightElbowBend = this.calculateJointAngle(rightShoulder, rightElbow, rightWrist);
      const avgElbowBend = (leftElbowBend + rightElbowBend) / 2;
      const straightnessScore = Math.min(100, Math.max(0, (avgElbowBend / 180) * 100));
      
      // Store positions for history tracking
      const lastPositions = [...(this.stateData.lastPositions || [])];
      if (lastPositions.length >= 5) {
        lastPositions.shift();
      }
      lastPositions.push({
        leftAngle: smoothedLeftAngle,
        rightAngle: smoothedRightAngle,
        avgAngle: avgAbductionAngle,
        timestamp
      });
      
      // Prepare final metrics
      const metrics = {
        shoulderCenter,
        shoulderWidth,
        leftAbductionAngle: smoothedLeftAngle,
        rightAbductionAngle: smoothedRightAngle,
        avgAbductionAngle,
        angleDifference,
        previousLeftAngle: smoothedLeftAngle,
        previousRightAngle: smoothedRightAngle,
        isMovingOut,
        isMovingIn,
        lastPositions,
        rangeOfMotion,
        symmetryScore,
        straightnessScore,
        velocityScore,
        lateralScore,
        lateralRatio,
        invalidMotionCount,
        stableUpFrames,
        stableDownFrames,
        wristsAboveShoulder,
        wristsBelowShoulder,
        previousTimestamp: timestamp
      };
      
      // Store last valid metrics for handling empty frames
      this.stateData.lastValidMetrics = {...metrics};
      
      // Debug logging
      if (shouldLogDebug) {
        console.debug(`Frame ${this.stateData.frameCount}: Angles L:${smoothedLeftAngle.toFixed(1)} R:${smoothedRightAngle.toFixed(1)} Avg:${avgAbductionAngle.toFixed(1)} Diff:${angleDifference.toFixed(1)}`);
        console.debug(`Motion: ${isMovingOut ? '↑' : isMovingIn ? '↓' : '-'} Delta:${angleDelta.toFixed(2)} Lat:${lateralRatio.toFixed(2)}`);
        console.debug(`State: ${this.currentState}, Above:${wristsAboveShoulder}, Below:${wristsBelowShoulder}`);
      }
      
      return metrics;
    } catch (error) {
      console.error('Error in computeMetrics:', error);
      
      // Return safe fallback metrics
      return {
        avgAbductionAngle: 0,
        leftAbductionAngle: 0,
        rightAbductionAngle: 0,
        angleDifference: 0,
        isMovingOut: false,
        isMovingIn: false,
        rangeOfMotion: 0,
        symmetryScore: 100,
        lateralScore: 100,
        lateralRatio: 1.0,
        wristsAboveShoulder: false,
        wristsBelowShoulder: true,
        previousTimestamp: timestamp
      };
    }
  }
  
  updateFormScores(metrics, transitioned) {
    if (!metrics || (!metrics.rangeOfMotion && metrics.rangeOfMotion !== 0)) return;
    
    try {
      // Update component scores
      this.formScores.symmetry = metrics.symmetryScore || 0;
      this.formScores.control = ((metrics.lateralScore || 0) + (metrics.straightnessScore || 0)) / 2;

      // ROM: Always show CURRENT/REAL-TIME ROM for live feedback
      this.formScores.rangeOfMotion = Math.round(metrics.rangeOfMotion || 0);
      
      const currentFormScore = Math.round(
        (this.formScores.rangeOfMotion * 0.4) + 
        (this.formScores.symmetry * 0.3) + 
        ((metrics.lateralScore || 0) * 0.3)
      );
      
      // Update overall form score
      if (this.stateData.repFormScore) {
        // If rep just completed, blend the rep score into overall
        this.formScores.overall = Math.round(this.formScores.overall * 0.7 + this.stateData.repFormScore * 0.3);
        this.stateData.repFormScore = 0;  // Reset after use so next frame uses current metrics
      } else {
        // Otherwise, use current form score for real-time feedback
        this.formScores.overall = currentFormScore;
      }
    } catch (error) {
      console.error('Error in updateFormScores:', error);
    }
  }
  
  generateFormFeedback(metrics, currentState) {
    if (!metrics || (currentState !== ArmAbductionState.UP && metrics.avgAbductionAngle < MIN_ABDUCTION_ANGLE * 2)) return null;
    
    try {
      if (metrics.lateralRatio < LATERAL_RATIO_THRESHOLD) {
        return "Move arms directly out to the sides, like wings";
      }
      
      if (metrics.angleDifference > ANGLE_DIFF_THRESHOLD) {
        return "Keep both arms at the same height";
      }
      
      if (metrics.straightnessScore < 70) {
        return "Keep arms straighter during the wing-flapping motion";
      }
      
      if (metrics.rangeOfMotion < 85 && currentState === ArmAbductionState.UP) {
        return "Raise arms higher in a complete wing-flapping motion";
      }
      
      if (currentState === ArmAbductionState.LOWERING && !metrics.wristsBelowShoulder) {
        return "Lower your arms fully below your shoulders";
      }
    } catch (error) {
      console.error('Error in generateFormFeedback:', error);
    }
    
    return null;
  }
  
  updateStateData(metrics, timestamp) {
    // Handle case where metrics is empty or undefined
    if (!metrics || Object.keys(metrics).length === 0) {
      if (DEBUG) {
        console.warn('Empty metrics in updateStateData');
      }
      return;
    }
    
    try {
      super.updateStateData(metrics, timestamp);
      
      // Track maximum ROM achieved during ABDUCTING and UP (while arms are elevated, not during lowering)
      if (this.currentState === ArmAbductionState.ABDUCTING || this.currentState === ArmAbductionState.UP) {
        this.stateData.maxRangeOfMotion = Math.max(
          this.stateData.maxRangeOfMotion || 0,
          metrics.rangeOfMotion || 0
        );
      }
      
      // Store wrist position states
      this.stateData.wristsAboveShoulder = metrics.wristsAboveShoulder;
      this.stateData.wristsBelowShoulder = metrics.wristsBelowShoulder;
      
      // Reset to DOWN state if stuck in UP state too long
      if (this.currentState === ArmAbductionState.UP && 
          timestamp - this.stateData.lastArmsUpTime > 3000) {
        this.currentState = ArmAbductionState.DOWN;
        if (DEBUG) console.debug('Resetting to DOWN: UP state timeout');
      }
      
      // Reset to DOWN state if form is consistently poor
      if (metrics.invalidMotionCount > CONSECUTIVE_INVALID_FRAMES && this.currentState !== ArmAbductionState.DOWN) {
        this.currentState = ArmAbductionState.DOWN;
        this.stateData.invalidMotionCount = 0;
        if (DEBUG) console.debug('Resetting to DOWN: Poor form');
      }
      
      // Handle case when movement stalls in ABDUCTING or LOWERING state
      if ((this.currentState === ArmAbductionState.ABDUCTING || this.currentState === ArmAbductionState.LOWERING) && 
          Math.abs(metrics.avgAbductionAngle - ((metrics.previousLeftAngle + metrics.previousRightAngle) / 2)) < MOVEMENT_THRESHOLD / 2) {
        
        this.stateData.staleFrameCount = (this.stateData.staleFrameCount || 0) + 1;
        
        // If stalled for too long, return to the most appropriate state
        if (this.stateData.staleFrameCount > 15) {
          // Use angle-based detection instead of wrist position for more reliability
          const newState = metrics.avgAbductionAngle < MIN_ABDUCTION_ANGLE * 2 ? ArmAbductionState.DOWN : 
                         metrics.avgAbductionAngle > TARGET_ABDUCTION_ANGLE * 0.7 ? ArmAbductionState.UP : 
                         this.currentState;
          
          if (newState !== this.currentState && DEBUG) {
            console.debug(`Resolving stall: ${this.currentState} → ${newState}`);
          }
          
          this.currentState = newState;
          this.stateData.staleFrameCount = 0;
        }
      } else {
        this.stateData.staleFrameCount = 0;
      }
      
      // Additional safety: if user is clearly in DOWN position for a while, reset to DOWN state
      if (this.currentState !== ArmAbductionState.DOWN && 
          metrics.avgAbductionAngle < MIN_ABDUCTION_ANGLE * 0.8 && 
          metrics.stableDownFrames > 10) {
        if (DEBUG) console.debug('Resetting to DOWN: Stable down position');
        this.currentState = ArmAbductionState.DOWN;
      }
    } catch (error) {
      console.error('Error in updateStateData:', error);
    }
  }
  
  calculateJointAngle(a, b, c) {
    try {
      if (!a || !b || !c) return 180; // Default to straight if any point is missing
      
      const vectorBA = {
        x: a.x - b.x,
        y: a.y - b.y,
        z: a.z - b.z
      };
      
      const vectorBC = {
        x: c.x - b.x,
        y: c.y - b.y,
        z: c.z - b.z
      };
      
      const dotProduct = vectorBA.x * vectorBC.x + vectorBA.y * vectorBC.y + vectorBA.z * vectorBC.z;
      
      const magnitudeBA = Math.sqrt(vectorBA.x * vectorBA.x + vectorBA.y * vectorBA.y + vectorBA.z * vectorBA.z);
      const magnitudeBC = Math.sqrt(vectorBC.x * vectorBC.x + vectorBC.y * vectorBC.y + vectorBC.z * vectorBC.z);
      
      if (magnitudeBA < 0.001 || magnitudeBC < 0.001) return 180;
      
      const angle = Math.acos(Math.min(1, Math.max(-1, dotProduct / (magnitudeBA * magnitudeBC)))) * (180 / Math.PI);
      
      return angle;
    } catch (error) {
      console.error('Error in calculateJointAngle:', error);
      return 180;
    }
  }
} 